import { useEffect } from 'react'

import { Cookies } from 'services/cookies'
import { useIsAnalyticsAllowed } from 'hooks/useIsAnalyticsAllowed'

/**
 * Maximum size for cookie values (4KB limit minus overhead for name and attributes)
 */
const MAX_COOKIE_SIZE = 3072 // 3KB to leave room for cookie name and attributes

/**
 * Sanitizes input to prevent XSS attacks and ensure safe cookie storage
 * @param {string} value - The value to sanitize
 * @returns {string} The sanitized value
 */
const sanitizeValue = (value) => {
  if (typeof value !== 'string') {
    value = String(value || '')
  }

  // Remove potentially dangerous characters that could be used for XSS
  return value
    .replace(/[<>'"&]/g, '') // Remove HTML/script injection characters
    .replace(/[\r\n\t]/g, ' ') // Replace line breaks and tabs with spaces
    .trim()
    .slice(0, MAX_COOKIE_SIZE) // Truncate to prevent cookie size limit issues
}

/**
 * Checks if cookies are enabled
 */
const areCookiesEnabled = () => {
  try {
    const testCookie = `calendly_test_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`
    document.cookie = `${testCookie}=test; path=/`
    const enabled = document.cookie.indexOf(testCookie) !== -1
    document.cookie = `${testCookie}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT`
    return enabled
  } catch (error) {
    return false
  }
}

/**
 * Stores a value in cookies for cross-application use
 */
const SharedStorage = ({
  keyName = '',
  expirationDays = 1,
  prefix = 'calendly_',
  value = ''
}) => {
  // Check analytics/targeting cookie consent and preview mode
  const isAnalyticsAllowed = useIsAnalyticsAllowed()

  useEffect(() => {
    if (!keyName) return

    // Check both browser cookie support and user consent
    if (!isAnalyticsAllowed || !areCookiesEnabled()) {
      return
    }

    const cookies = new Cookies()
    const key = `${prefix}${keyName}`
    const sanitizedValue = sanitizeValue(value)

    if (!sanitizedValue) {
      cookies.removeCookie(key)
      return
    }

    // Limit expiration for PII to 30 days maximum for compliance
    const maxExpiration = Math.min(expirationDays, 30)

    cookies.setCookie(key, sanitizedValue, {
      expires: maxExpiration,
      sameSite: 'Lax', // Prevent CSRF while allowing normal navigation
      secure: true // Only send over HTTPS in production
    })
  }, [keyName, expirationDays, prefix, value, isAnalyticsAllowed])

  return null
}

/**
 * Gets count of cookies matching the keyName pattern
 */
export const getCookieCount = (keyName, prefix = 'calendly_') => {
  const searchPrefix = `${prefix}${keyName}_`

  const allCookies = {}
  document.cookie.split(';').forEach((cookie) => {
    const trimmedCookie = cookie.trim()
    const equalsIndex = trimmedCookie.indexOf('=')

    if (equalsIndex > 0) {
      const name = trimmedCookie.slice(0, equalsIndex)
      const value = trimmedCookie.slice(equalsIndex + 1)
      allCookies[name] = decodeURIComponent(value)
    }
  })

  return Object.keys(allCookies).filter((cookieName) => cookieName.startsWith(searchPrefix)).length
}

/**
 * Stores a counter cookie for event tracking and duplicate prevention
 */
export const CookieCounter = ({
  keyName = '',
  id = '',
  expires = 30,
  prefix = 'calendly_',
  onUpdate
}) => {
  // Check analytics/targeting cookie consent and preview mode
  const isAnalyticsAllowed = useIsAnalyticsAllowed()

  useEffect(() => {
    if (!keyName || !id) return

    // Check both browser cookie support and user consent
    if (!areCookiesEnabled() || !isAnalyticsAllowed) {
      return
    }

    const cookies = new Cookies()
    const sanitizedId = sanitizeValue(id)
    const key = `${prefix}${keyName}_${sanitizedId}`
    const count = getCookieCount(keyName, prefix)

    if (cookies.getCookie(key)) return

    try {
      cookies.setCookie(key, new Date().toISOString(), {
        expires,
        sameSite: 'Lax',
        secure: true
      })
      if (onUpdate) onUpdate(count + 1)
    } catch (error) {
      // Silently handle cookie storage failures to prevent breaking functionality
    }
  }, [keyName, id, expires, prefix, onUpdate, isAnalyticsAllowed])

  return null
}

/**
 * A Component that stores an object as a series of cookies for use in other applications
 * @param {object} value - The object to store as cookies
 */
export const SharedObjectStorage = ({
  value = {}
}) => (
  <>
    {Object.entries(value).map(([_key, _value]) => (
      <SharedStorage key={_key} keyName={_key} value={_value} />
    ))}
  </>
)
